/**
 * Multimedia Grid
 */
import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, getViewportSize, throttle } from 'js/helpers.js';
import Glide from '@glidejs/glide';
const componentName = 'multimedia-grid';

class MultimediaGrid extends Component {

    /**
     * Component Setup
     */

    // Elements
    elements = {
        container: this.element.querySelector(`.${componentName}__container`),
        thumbnailList: this.element.querySelector(`.${componentName}__thumbnail-list`),
        thumbnails: this.element.getElementsByClassName(`${componentName}__thumbnail`),
        activeMediaParent: this.element.querySelector(`.${componentName}__active-media`),
        prevButton: this.element.querySelector(`.${componentName}__previous`),
        nextButton: this.element.querySelector(`.${componentName}__next`),
        mobileSlider: this.element.querySelector(`.${componentName}__mobile-slider`)
    };

    // Util & State
    resizeThrottleMS = 100;
    mediaTransitionMS = 200;

    currentViewport = getViewportSize();
    currentWindowWidth = window.innerWidth;

    thumbnailHeight = 92;
    sliderPositionX = 0;
    totalSlides = this.elements.thumbnails.length;

    glideInstance = null;
    glideOptions = {
        type: 'slider',
        gap: 5,
        rewind: false,
    };

    /**
     * Update Mobile Carousel
     */
    updateMobileCarousel = () => {
        if(this.currentViewport === 'desktop') {
            if(this.glideInstance) {
                this.glideInstance.destroy();
                this.glideInstance = null;
            }

            this.elements.thumbnails[0].click();
        }

        if(!this.glideInstance && this.currentViewport === 'mobile') {
            this.glideInstance = new Glide(`.${componentName}__thumbnail-track.glide`, this.glideOptions).mount();
        }
    };

    /**
     * Populate Active Media
     * @param { DOM Element } thumbnailEle - thumbnail element to show the selected active media
     */
    populateActiveMedia = (thumbnailEle) => {
        const slideId = thumbnailEle.getAttribute('data-slide');
        $(`.${componentName}__active-media`).addClass("hidden"); // eslint-disable-line
        this.element.querySelector(`[id="${slideId}"]`).classList.remove('hidden');
        // Recalibrate video size
        if (typeof (Event) === 'function') {
            // modern browsers
            window.dispatchEvent(new Event('resize'));
        } else {
            // for IE and other old browsers
            // causes deprecation warning on modern browsers
            const evt = window.document.createEvent('UIEvents');
            evt.initUIEvent('resize', true, false, window, 0);
            window.dispatchEvent(evt);
        }
    };

    /**
     * Update Desktop Carousel
     * @param { String } direction - pass in 'previous' or 'next' to transition slides
     */
    updateDesktopCarousel = (direction) => {
        if(this.totalSlides > 5) {
            const maxTransition = -((this.totalSlides - 5) * this.thumbnailHeight);
            const prevTransition = this.sliderPositionX + this.thumbnailHeight;
            const nextTransition = this.sliderPositionX - this.thumbnailHeight;

            // Update Direction if passed in
            if(direction) {
                this.sliderPositionX = direction === 'next' ? nextTransition : prevTransition;
                this.elements.thumbnailList.style.transform = `translateY(${this.sliderPositionX}px)`;
            }

            // Update Show/Hide of Buttons
            this.elements.prevButton.classList.remove('active');
            this.elements.nextButton.classList.remove('active');

            if(this.sliderPositionX === 0) this.elements.nextButton.classList.add('active');
            if(this.sliderPositionX < 0) {
                this.elements.prevButton.classList.add('active');
                this.elements.nextButton.classList.add('active');
            }
            if(this.sliderPositionX === maxTransition) this.elements.nextButton.classList.remove('active');
        }
    };

    /**
     * On Thumbnail Click (Desktop)
     */
    onThumbnailClick = (e) => {
        const thumbnailEle = e.currentTarget;
        if(!thumbnailEle.classList.contains('active') && e.isTrusted) {
            [...this.elements.thumbnails].map((ele) => ele.classList.remove('active'));
            this.populateActiveMedia(thumbnailEle);
            thumbnailEle.classList.add('active');
        }
    };

    /**
     * On Window Resize
     */
    onWindowResize = () => {
        if(window.innerWidth === this.currentWindowWidth) return;
        this.currentWindowWidth = window.innerWidth;
        this.currentViewport = getViewportSize();

        setTimeout(() => this.updateMobileCarousel(), 1);
    };

    /**
     * Add Event Listeners
     */
    addEventListeners = () => {
        // Global Listeners
        window.addEventListener('resize', throttle(this.onWindowResize, this.resizeThrottleMS));

        // Component Listeners
        [...this.elements.thumbnails].map((ele) => ele.addEventListener('click', (e) => {
            if(this.currentViewport === 'desktop') this.onThumbnailClick(e);
        }));
        this.elements.prevButton.addEventListener('click', () => this.updateDesktopCarousel('previous'));
        this.elements.nextButton.addEventListener('click', () => this.updateDesktopCarousel('next'));
        if (this.totalSlides > 5) {
            this.elements.nextButton.classList.add('active');
        }
    };

    /**
     * Initialize Component
     */
    init() {
        // Remove default carousel class on load
        this.element.classList.remove('carousel');

        // Fire Init Funcs
        if(!isEditModeFooter() && this.elements.thumbnails.length > 0) {
            this.addEventListeners();
            this.updateMobileCarousel();
        }
    }
}

registerComponent(`.${componentName}`, MultimediaGrid);
